import { getCrmColors } from "../../theme/crmColors";
import { Chip, Paper, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { fetchContactTagsContext } from "../../services/contactTags";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: 0,
        background: "transparent",
        border: 0,
        boxShadow: "none",
    },
    field: {
        "& .MuiOutlinedInput-root": {
            minHeight: 38,
            borderRadius: 8,
            color: theme.mode === "light" ? "#172033" : (theme.ticketDark ? "#FFFFFF" : "#f8fafc"),
            backgroundColor: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").surface : "#2f2f2f"),
            "& fieldset": {
                borderColor: theme.mode === "light" ? "#d7e1ee" : (theme.ticketDark ? getCrmColors(theme, "dark").border : "rgba(255,255,255,0.10)"),
                borderWidth: 1,
            },
            "&:hover fieldset": {
                borderColor: theme.mode === "light" ? "#cbd5e1" : "rgba(255,255,255,0.18)",
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.mode === "light" ? "#cbd5e1" : "rgba(255,255,255,0.18)",
                borderWidth: 1,
            },
        },
        "& .MuiInputBase-input::placeholder": {
            color: theme.mode === "light" ? "#64748b" : (theme.ticketDark ? "#FFFFFF" : "#cbd5e1"),
            opacity: 1,
        },
    },
    tag: {
        color: "#FFF",
        marginRight: 4,
        padding: 1,
        fontWeight: "bold",
        paddingLeft: 5,
        paddingRight: 5,
        borderRadius: 6,
        fontSize: "0.8em",
        whiteSpace: "nowrap",
        border: 0,
    },
    optionsPaper: {
        width: 400,
        marginLeft: 6,
        background: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").inset : "#343434"),
        color: theme.mode === "light" ? "#172033" : (theme.ticketDark ? "#FFFFFF" : "#f8fafc"),
    },
}));

export function TagsContainer({ contact, ticketId, blocked = false }) {
    const classes = useStyles();

    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const [busy, setBusy] = useState(false), [ready, setReady] = useState(false);
    const isMounted = useRef(true);
    const recipient = useRef(), request = useRef(0), busyRef = useRef(false);
    recipient.current = { contactId: contact.id, ticketId, blocked };
    const normalTags = value => Array.isArray(value) ? value.filter(tag => tag && Number(tag.kanban || 0) === 0) : [];
    const active = (id, ticket) => isMounted.current && !recipient.current.blocked && recipient.current.contactId === id && recipient.current.ticketId === ticket;

    useEffect(() => {
        return () => {
            isMounted.current = false; request.current += 1;
        }
    }, [])

    useEffect(() => {
        busyRef.current = false; setBusy(false); setReady(false); setTags([]); setSelecteds(normalTags(contact.tags));
        request.current += 1;
        if (contact.id && !blocked) loadTags();
        // Catalog is stable for this customer; message/profile updates must not reload it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact.id, ticketId, blocked]);
    useEffect(() => {
        if (!busyRef.current) setSelecteds(normalTags(contact.tags));
    }, [contact.id, contact.tags]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            throw err;
        }
    }

    const loadTags = async () => {
        const id = contact.id, ticket = ticketId, read = ++request.current;
        try {
            const { data } = ticket ? await fetchContactTagsContext(ticket) : await api.get(`/tags/list`, { params: { kanban: 0 } });
            if (!active(id, ticket) || read !== request.current) return;
            if (ticket && (Number(data.ticketId) !== Number(ticket) || Number(data.contactId) !== Number(id))) throw new Error("Contexto de tags inválido.");
            const options = ticket ? data.tags : data;
            setTags(options);
            if (ticket) setSelecteds(options.filter(tag => data.selectedTagIds.includes(Number(tag.id))));
            setReady(true);
        } catch (err) {
            if (active(id, ticket) && read === request.current) { setReady(false); toastError(err); }
        }
    }

    const syncTags = async (data) => {
        const { data: responseData } = await api.post(`/tags/sync`, data);
        return responseData;
    }

    const onChange = async (value, reason) => {
        if (blocked || !ready || busyRef.current || !contact.id) return;
        const id = contact.id, ticket = ticketId, previous = selecteds;
        busyRef.current = true; setBusy(true);
        let optionsChanged = [];
        try {
          if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (isString(item)) {
                        if (item.trim().length < 3) throw new Error("Tag muito curta!");
                        const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            if (!active(id, ticket)) return;
            await loadTags();
          } else {
            optionsChanged = value;
          }
          if (!active(id, ticket)) return;
          optionsChanged = normalTags(optionsChanged);
          const expectedTagIds = [...new Set(previous.map(tag => Number(tag.id)))].sort((a,b) => a-b);
          const updated = await syncTags({ contactId: id, tags: optionsChanged, expectedTagIds });
          if (active(id, ticket)) setSelecteds(normalTags(updated.tags));
        } catch (err) {
          if (active(id, ticket)) {
            setSelecteds(previous); toastError(err);
            if (ticket) await loadTags();
            else if (err.response?.status === 409) {
              try { const { data } = await api.get(`/contacts/${id}`); if (active(id, ticket)) setSelecteds(normalTags(data.tags)); } catch (readError) { if (active(id, ticket)) { setReady(false); toastError(readError); } }
            }
          }
        } finally { if (active(id, ticket)) { busyRef.current = false; setBusy(false); } }
    }

    function getRandomHexColor() {
        // Gerar valores aleatórios para os componentes de cor
        const red = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const green = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const blue = Math.floor(Math.random() * 256); // Valor entre 0 e 255
      
        // Converter os componentes de cor em uma cor hexadecimal
        const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
      
        return hexColor;
    }

    return (
        <Paper className={classes.root}>
            <Autocomplete
                multiple
                size="small"
                options={tags}
                value={selecteds}
                disabled={blocked || busy || !ready}
                freeSolo
                onChange={(e, v, r) => onChange(v, r)}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, selected) => Number(option.id) === Number(selected.id)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            variant="default"
                            className={classes.tag}
                            style={{
                                backgroundColor: option.color || '#eee',
                            }}
                            label={option.name}
                            {...getTagProps({ index })}
                            size="small"
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField {...params} className={classes.field} variant="outlined" placeholder="Tags" />
                )}
                PaperComponent={({ children }) => (
                    <Paper className={classes.optionsPaper}>
                        {children}
                    </Paper>
                )}
            />
        </Paper>
    )
}
